import { FunctionComponent, ReactNode, useState } from 'react';
import { PageTitle } from './page-title';
import { Box, Button, Chip, ListItemIcon, Menu, MenuItem, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { BackButtonLink } from './back-button-link';
import gaiaTheme from '../../../root-components/theme';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { IconPicker } from './icon-picker';
import { LoadingButton } from '@mui/lab';
import { DownloadOutlined, KeyboardArrowDownOutlined } from '@mui/icons-material';

export type PageHeadingMenuAction = {
  label: string,
  onClick: () => void,
  icon?: ReactNode,
  disabled?: boolean,
};

export const PageHeading: FunctionComponent<{
  title: string,
  hasTabs?: boolean,
  includeBackButton?: boolean,
  backButtonText?: string,
  backButtonLink?: string,
  includeActionButton?: boolean,
  actionButtonText?: string,
  actionButtonVariant?: 'text' | 'outlined' | 'contained',
  actionButtonIcon?: IconName,
  hideShowActionButton?: boolean,
  disableActionButton?: boolean,
  disabledActionButtonTooltip?: string,
  actionButtonHandler?: any,
  makeActionButtonLoading?: boolean;
  includeSecondaryActionButton?: boolean;
  loadingStatus?: boolean,
  actionButtonTextLoading?: string,
  actionButtonTextNotLoading?: string,
  secondaryActionButtonText?: string,
  secondaryActionButtonVariant?: 'text' | 'outlined' | 'contained',
  secondaryActionButtonIcon?: IconName,
  hideShowSecondaryActionButton?: boolean,
  disableSecondaryActionButton?: boolean,
  secondaryActionButtonHandler?: any,
  showDownloadButton?: boolean,
  downloadCsvHandler?: (e: React.MouseEvent<HTMLElement>) => void,
  /** Extra actions collapsed into a single dropdown at the end of the header row. */
  menuActions?: PageHeadingMenuAction[],
  menuButtonText?: string,
  showChip?: boolean,
  chipColor?: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning",
  chipLabel?: string,
  extraActions?: ReactNode,
}> = ({
  title,
  hasTabs = false,
  includeBackButton = false,
  backButtonText,
  backButtonLink,
  includeActionButton = false,
  actionButtonText,
  actionButtonVariant = 'text',
  actionButtonIcon,
  hideShowActionButton = false,
  disableActionButton = false,
  disabledActionButtonTooltip,
  actionButtonHandler,
  makeActionButtonLoading = false,
  loadingStatus,
  actionButtonTextLoading,
  actionButtonTextNotLoading,
  includeSecondaryActionButton = false,
  secondaryActionButtonText,
  secondaryActionButtonVariant = 'text',
  secondaryActionButtonIcon,
  hideShowSecondaryActionButton = false,
  disableSecondaryActionButton = false,
  secondaryActionButtonHandler,
  showDownloadButton = false,
  downloadCsvHandler,
  menuActions,
  menuButtonText = 'Actions',
  showChip = false,
  chipColor,
  chipLabel,
  extraActions,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);

    return (
      <Box>
        {includeBackButton && backButtonText && backButtonLink &&
          // mb 2 matches the pages that compose BackButtonLink by hand (the emissions group
          // page): the back link and the title need air between them, not a caption gap.
          <Box sx={{ mb: 2, display: 'inline-block' }}>
            <BackButtonLink buttonText={backButtonText} link={backButtonLink} />
          </Box>}
        <Stack direction={isMobileMode ? 'column' : 'row'} justifyContent='space-between' alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ mb: hasTabs ? 1 : 2 }}>
          <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <PageTitle title={title} />
            {showChip && chipColor && chipLabel && <Chip variant='outlined' color={chipColor} label={chipLabel} sx={{ ml: 2 }} />}
          </Stack>
          <Stack
            display='flex'
            flexDirection='row'
            flexWrap='wrap'
            useFlexGap
            spacing={1}
            sx={{ justifyContent: 'flex-end', alignItems: 'center', maxWidth: '100%' }}
          >
            {extraActions}
            {showDownloadButton &&
              <Button
                onClick={downloadCsvHandler}
                variant='outlined'
                startIcon={<DownloadOutlined />}
                sx={{ mr: 1 }}>
                <Typography sx={{ display: 'inline' }}>Download CSV</Typography>
              </Button>
            }
            {includeActionButton && actionButtonText && actionButtonIcon && !hideShowActionButton && actionButtonHandler && (() => {
              const button = (
                <Button
                  variant={actionButtonVariant}
                  onClick={actionButtonHandler}
                  sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                  disabled={disableActionButton}
                  startIcon={<IconPicker icon={actionButtonIcon} />}
                >
                  <Typography>{actionButtonText}</Typography>
                </Button>
              );

              if (!disableActionButton || !disabledActionButtonTooltip) return button;

              // MUI Tooltip doesn't fire on disabled buttons; wrap in span.
              return (
                <Tooltip title={disabledActionButtonTooltip}>
                  <span>{button}</span>
                </Tooltip>
              );
            })()}
            {includeActionButton && actionButtonTextLoading && actionButtonTextNotLoading && actionButtonIcon && !hideShowActionButton && actionButtonHandler && makeActionButtonLoading &&
              <LoadingButton
                variant={actionButtonVariant}
                onClick={actionButtonHandler}
                loading={loadingStatus}
                startIcon={<IconPicker icon={actionButtonIcon} />}
                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                disabled={disableActionButton}>
                <Typography>{loadingStatus ? actionButtonTextLoading : actionButtonTextNotLoading}</Typography>
              </LoadingButton>
            }
            {includeSecondaryActionButton && secondaryActionButtonText && secondaryActionButtonIcon && !hideShowSecondaryActionButton && secondaryActionButtonHandler &&
              <Button variant={secondaryActionButtonVariant} onClick={secondaryActionButtonHandler} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} disabled={disableSecondaryActionButton} startIcon={<IconPicker icon={secondaryActionButtonIcon} />}>
                <Typography>{secondaryActionButtonText}</Typography>
              </Button>
            }
            {menuActions && menuActions.length > 0 &&
              <>
                <Button
                  variant='outlined'
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  endIcon={<KeyboardArrowDownOutlined />}
                  aria-haspopup='true'
                  aria-expanded={menuOpen ? 'true' : undefined}
                  sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                >
                  <Typography>{menuButtonText}</Typography>
                </Button>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={() => setMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {menuActions.map((action) => (
                    <MenuItem
                      key={action.label}
                      disabled={action.disabled}
                      onClick={() => { setMenuAnchorEl(null); action.onClick(); }}
                    >
                      {action.icon && <ListItemIcon sx={{ color: 'text.primary' }}>{action.icon}</ListItemIcon>}
                      <Typography>{action.label}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            }
          </Stack>
        </Stack>
      </Box>
    )
  }